import React, { useEffect, useMemo, useState } from "react";
import { Search, BellRing } from "lucide-react";
import { useNavigate } from "react-router-dom";
import RowActions from "@/components/RowActions";
import EditReservationDialog, { useBookingData } from "@/components/EditReservationDialog";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty, ResBadge, useQueryFilter, matchesQuery, interimDue } from "@/pages/shared";
import { useCamp } from "@/context/SettingsContext";

export default function Verblijven() {
  const { t } = useLang();
  const { camp } = useCamp();
  const interimDays = camp?.interim_reminder_days || 30;
  const [rows, setRows] = useState([]);
  const [q, setQ] = useQueryFilter();
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();
  const { guests, zones, pitches } = useBookingData();

  const load = () => api.get("/reservations?filter=staying").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const list = rows.filter((r) => matchesQuery(r, q));
    return [...list].sort((a, b) => (a.guest_name || "").localeCompare(b.guest_name || ""));
  }, [rows, q]);

  return (
    <Panel
      title={`${t("nav_verblijven")} (${filtered.length}${q ? `/${rows.length}` : ""})`}
      testid="staying-panel"
      action={
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_staying")} className="pl-8" data-testid="staying-search" />
        </div>
      }
    >
      {filtered.length === 0 ? <Empty>{t("no_staying")}</Empty> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("guest")}</TableHead>
              <TableHead>{t("pitch")}</TableHead>
              <TableHead>{t("arrival")}</TableHead>
              <TableHead>{t("departure")}</TableHead>
              <TableHead>{t("persons")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`staying-row-${r.id}`}>
                <TableCell className="font-medium">{r.guest_name}{r.booker?.nickname ? <span className="text-muted-foreground"> · {r.booker.nickname}</span> : null}</TableCell>
                <TableCell><span className="font-semibold text-primary">{r.pitch_label}</span></TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(r.arrival)}</TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(r.departure)}</TableCell>
                <TableCell>{r.persons}</TableCell>
                <TableCell>
                  <ResBadge status={r.status} />
                  {interimDue(r, interimDays) && (
                    <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`staying-interim-due-${r.id}`}>
                      <BellRing className="h-3 w-3" />{t("interim_due")}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right"><RowActions r={r} onEdit={setEditing} onCheckout={(x) => navigate(`/vertrekkend?id=${x.id}`)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <EditReservationDialog reservation={editing} onClose={() => setEditing(null)} onSaved={load} guests={guests} zones={zones} pitches={pitches} withDocuments />
    </Panel>
  );
}
